import React, { Component } from "react";
import { Card, CardBody, CardHeader, Button, Input } from "reactstrap";
import DateRangePicker from "react-dates/lib/components/DateRangePicker";
import "react-datepicker/dist/react-datepicker.css";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import reports from "@/api/reports";
import swal from "sweetalert2";
import moment from "moment-timezone";
import { APP_CONST, getStoredItem } from "@/store/dataStore";
import { handleError } from "@/util/errorHandler";
import { saveAs } from "file-saver";
import LoadingOverlay from "@/views/Components/LoadingOverlay";

class Download extends Component {
  state = {
    statusOptions: [],
    selectedStatus: "",
    from: moment(),
    to: moment(),
    filter: "",
    contents: [],
    expandedRowsIds: [],
    pagination: {
      hasNext: false,
      hasPrevious: false,
      size: 10,
      number: 0,
    },
    user_permissions: JSON.parse(getStoredItem(APP_CONST.USER_PERMISSIONS)),
    user_authorities: JSON.parse(getStoredItem(APP_CONST.USER_AUTHORITIES)),
    refresh: true,
    loading: false,
  };

  interval = null;

  bindInnerTableRef = innerComponent => {
    this.table = innerComponent;
  };

  onSearch = () => {
    this.loadPagination(0);
  };

  onSelectDate = date => {
    const currentYear = new Date().getFullYear();
    const year =
      new Date(date).getFullYear() > currentYear
        ? currentYear
        : new Date(date).getFullYear();
    const newDate = new Date(date).setFullYear(year);

    this.setState({
      generateDateTime: newDate,
    });
  };

  handleStatusChange = event => {
    this.setState({
      selectedStatus: event.target.value,
    });
  };

  loadPagination = async (
    page = this.state.pagination.number,
    size = this.state.pagination.size,
    sort = "createdAt,desc",
  ) => {
    this.setState({ loading: true });
    const dateFrom = this.state.from
      ? this.state.from.format("YYYY-MM-DD")
      : null;
    const dateTo = this.state.to ? this.state.to.format("YYYY-MM-DD") : null;
    const res = await reports
      .requestExports({
        page,
        size,
        filter: this.state.filter,
        status: this.state.selectedStatus,
        from: dateFrom,
        to: dateTo,
        sort: sort,
      })
      .catch(handleError)
      .finally(() => {
        this.setState({ loading: false });
      });
    this.setState({
      contents: res.data.data.content,
      pagination: res.data.data.page,
      loading: false,
    });
  };

  download = async (id, fileName) => {
    let content;
    try {
      content = await reports.requestDownloadExportedFile(id);
    } catch (err) {
      handleError(err, { title: `failed in downloading a report ${fileName}` });
      this.setState({ loading: false });
      return;
    }
    saveAs(new Blob([content]), `${fileName}`);
  };

  cancel = async id => {
    try {
      await reports.requestCancelExportedFile(id);
      await swal(
        "Success",
        "Your request to generate the statement has been canceled.",
        "success",
      );
      this.loadPagination();
    } catch (err) {
      handleError(err, {
        title: `failed to cancel a report ${id}`,
      });
      this.setState({ loading: false });
      return;
    }
  };

  renderActionButton = (cell, row) => {
    const { status, id, hasFile, fileName } = row;

    return (
      <>
        <Button
          color="primary"
          type="button"
          disabled={status !== "SUCCESS" || !hasFile}
          onClick={() => this.download(id, fileName)}
          style={{ marginRight: 10 }}>
          Download
        </Button>
        <Button
          color="primary"
          type="button"
          onClick={() => this.cancel(id)}
          disabled={!["REQUESTED", "QUERYING", "GENERATING"].includes(status)}>
          Cancel
        </Button>
      </>
    );
  };

  toggleRefresh = () => {
    this.setState({ refresh: !this.state.refresh });
  };

  toggleParams = row => {
    const { expandedRowsIds } = this.state;
    const index = expandedRowsIds.indexOf(row.id);
    if (index > -1) {
      expandedRowsIds.splice(index, 1);
    } else {
      expandedRowsIds.push(row.id);
    }
    this.setState({ expandedRowsIds });
  };

  renderParams = (cell, row) => {
    const { queryParams } = row;
    const showingParams = this.state.expandedRowsIds.includes(row.id)
      ? queryParams
          .replace(/,/g, ",\n")
          .replace(/{/g, "{\n")
          .replace(/}/g, "\n}")
      : "{...}";
    const icon = this.state.expandedRowsIds.includes(row.id) ? (
      <i
        className="fa fa-chevron-up"
        style={{ cursor: "pointer", marginRight: 5 }}
        onClick={() => this.toggleParams(row)}
      />
    ) : (
      <i
        className="fa fa-chevron-down"
        style={{ cursor: "pointer", marginRight: 5 }}
        onClick={() => this.toggleParams(row)}
      />
    );
    return (
      <>
        <div style={{ whiteSpace: "normal", display: "flex" }}>
          {icon}
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {showingParams}
          </pre>
        </div>
      </>
    );
  };

  onPrevious = () => {
    const { pagination } = this.state;
    const prevPage = pagination.number - 1;
    this.loadPagination(prevPage);
  };

  onNext = () => {
    const { pagination } = this.state;
    const nextPage = pagination.number + 1;
    this.loadPagination(nextPage);
  };

  onPageChange = (page, sizePerPage) => {
    this.loadPagination(page, sizePerPage);
  };

  onSortChange = (sortName, sortOrder) => {
    const sort = `${sortName},${sortOrder}`;
    this.loadPagination(0, this.state.pagination.size, sort);
  };

  componentDidMount() {
    this.loadPagination();

    // get report status
    reports.getExportStatus().then(res => {
      const options = [];
      options.push({ label: "ALL", value: "" });
      res.data.map(status =>
        options.push({
          label: status.id,
          value: status.id,
        }),
      );
      this.setState({ statusOptions: options });
    });

    // create refresh interval
    if (this.state.refresh) {
      this.interval = setInterval(() => {
        if (this.state.refresh && window.location.pathname === "/download") {
          this.loadPagination();
        }
      }, 5 * 1000); // 5 seconds interval
    }
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  render() {
    return (
      <Card>
        <CardHeader>Download</CardHeader>
        <div
          className="pt-3 px-4"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}>
          <div className="col-auto" style={{ alignContent: "center" }}>
            <input
              type="checkbox"
              className="mr-2"
              checked={this.state.refresh}
              onChange={this.toggleRefresh}
            />
            Automatically Refresh
          </div>
          <div
            className="col-10"
            style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="col-auto" style={{ justifyItems: "end" }}>
              <DateRangePicker
                startDate={this.state.from}
                startDateId="date_from"
                endDate={this.state.to}
                endDateId="date_to"
                onDatesChange={({ startDate, endDate }) =>
                  this.setState({ from: startDate, to: endDate })
                }
                focusedInput={this.state.focusedInput}
                onFocusChange={focusedInput => this.setState({ focusedInput })}
                showClearDates
                showDefaultInputIcon
                isOutsideRange={date =>
                  moment(date).isAfter(
                    moment.tz("Asia/Phnom_Penh").endOf("day"),
                  )
                }
                small
                minimumNights={0}
              />
            </div>
            <div className="col-2">
              <Input
                type="select"
                name="status"
                id="status"
                value={this.state.selectedStatus}
                style={{ height: 39 }}
                onChange={this.handleStatusChange}>
                {this.state.statusOptions.map(type => {
                  return (
                    <option value={type.value} key={type.value}>
                      {type.label}
                    </option>
                  );
                })}
              </Input>
            </div>
            <div className="col-3">
              <Input
                style={{ paddingRight: 100, height: 39 }}
                type="text"
                id="filter"
                placeholder="Search"
                onChange={e => this.setState({ filter: e.target.value })}
                onKeyDown={this.onEnterSearch}
                value={this.state.filter}
              />
            </div>
            <div className="col-auto">
              <Button
                className="p-0"
                color="primary"
                style={{
                  width: 100,
                  height: 35,
                }}
                onClick={this.onSearch}
                data-cy="submit">
                Search
              </Button>
            </div>
          </div>
        </div>
        <CardBody>
          <LoadingOverlay loading={this.state.loading} />
          <BootstrapTable
            version="4"
            striped
            hover
            condensed
            remote
            pagination={false}
            data={this.state.contents}
            options={{
              onPageChange: this.onPageChange,
              onSortChange: this.onSortChange,
            }}
            keyField="id">
            <TableHeaderColumn
              dataSort
              dataField="createdAt"
              dataAlign="center"
              width="100">
              Generate Date Time
            </TableHeaderColumn>
            <TableHeaderColumn dataSort dataField="reportType" width="150">
              Report Type
            </TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.renderParams} width="160">
              Query Params
            </TableHeaderColumn>
            <TableHeaderColumn dataSort dataField="status" width="100">
              Status
            </TableHeaderColumn>
            <TableHeaderColumn dataSort dataField="completedAt" width="120">
              Complete Date Time
            </TableHeaderColumn>
            <TableHeaderColumn dataSort dataField="fileName" width="300">
              Message
            </TableHeaderColumn>
            <TableHeaderColumn
              dataFormat={this.renderActionButton}
              width="150"
              dataAlign="center">
              Actions
            </TableHeaderColumn>
          </BootstrapTable>
          <div style={{ marginTop: 10, textAlign: "right" }}>
            <Button
              style={{ width: 100 }}
              onClick={this.onPrevious}
              disabled={this.state.pagination.hasPrevious === false}
              data-cy="back">
              <i className="fa fa-chevron-left" style={{ marginRight: 5 }} />
              Prev
            </Button>
            <Button
              color="primary"
              style={{ marginLeft: 20, width: 100 }}
              onClick={this.onNext}
              disabled={this.state.pagination.hasNext === false}
              data-cy="submit">
              Next
              <i className="fa fa-chevron-right" style={{ marginLeft: 5 }} />
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }
}

export default Download;
